export interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  metaDescription: string;
  category: string;
  readTime: string;
  publishedAt: string;
  author: string;
  authorRole: string;
  authorInitials: string;
  authorColor: string;
  image: string;
  tags: string[];
  featured?: boolean;
  content: BlogSection[];
}

export interface BlogSection {
  type: "paragraph" | "heading" | "subheading" | "list" | "callout" | "stat-grid";
  text?: string;
  items?: string[];
  stats?: { value: string; label: string }[];
}

export const blogCategories = [
  "All",
  "Lead Generation",
  "Sales Strategy",
  "Market Insights",
  "Operations",
  "Industry News",
];

export const blogPosts: BlogPost[] = [
  {
    slug: "how-to-close-solar-leads-faster",
    title: "How to Close Solar Leads 3x Faster: A Field-Tested Playbook",
    excerpt:
      "Most solar installers lose deals in the first 48 hours. Here's the exact follow-up sequence top closers use to convert high-intent leads before competitors even call back.",
    metaDescription:
      "Learn how to close solar leads 3x faster with this field-tested playbook. Discover the 6-touch follow-up sequence that drives 34% higher close rates for solar installers.",
    category: "Sales Strategy",
    readTime: "8 min read",
    publishedAt: "March 28, 2026",
    author: "Marcus Webb",
    authorRole: "Sales Director, SolarLeadSource",
    authorInitials: "MW",
    authorColor: "bg-[#16a34a]",
    image:
      "https://public.readdy.ai/ai/img_res/107ebf1bd24032f3366b5540a7357f2e.jpg",
    tags: ["closing", "follow-up", "conversion", "sales"],
    featured: true,
    content: [
      {
        type: "paragraph",
        text: "The solar industry has a dirty secret: most installers are sitting on a goldmine of leads they never properly work. Industry data shows that 78% of solar leads go cold within 72 hours — not because the homeowner lost interest, but because the installer failed to follow up with the right message at the right time.",
      },
      {
        type: "heading",
        text: "The 48-Hour Window Is Everything",
      },
      {
        type: "paragraph",
        text: "When a homeowner requests solar information, their intent is at its peak. They've just done the mental work of deciding they want to explore solar — your job is to meet them while that energy is still high. Every hour you wait, that intent decays.",
      },
      {
        type: "stat-grid",
        stats: [
          { value: "78%", label: "of leads go cold within 72 hours" },
          { value: "5x", label: "higher close rate when called within 1 hour" },
          { value: "3.2x", label: "more likely to close with 6+ touchpoints" },
          { value: "47%", label: "of installers never make a second call" },
        ],
      },
      {
        type: "heading",
        text: "The 6-Touch Follow-Up Sequence",
      },
      {
        type: "paragraph",
        text: "Top-performing installers don't just call once and give up. They use a structured multi-channel sequence that keeps them top of mind without being annoying. Here's the exact sequence that drives a 34% higher close rate:",
      },
      {
        type: "list",
        items: [
          "Hour 1 — Phone call + voicemail: Introduce yourself, mention you saw their inquiry, and give a specific reason to call back (e.g., \"I have a quote ready for your ZIP code\")",
          "Hour 3 — Text message: Short, personal, no links. \"Hi [Name], this is [You] from [Company]. Just left a voicemail — happy to answer any questions about going solar in [City].\"",
          "Hour 24 — Email: Send a value-first email with a local case study or savings estimate. No hard sell.",
          "Day 3 — Second phone call: Reference your previous attempts, ask if they have 5 minutes. If voicemail, leave a different message.",
          "Day 5 — Text with social proof: \"We just installed a system for a neighbor in [Neighborhood] — they're saving $180/month. Happy to share details.\"",
          "Day 10 — Final email: Low-pressure close. \"I don't want to keep bothering you — just wanted to make sure you had everything you need to make a decision. Here's a link to our reviews.\"",
        ],
      },
      {
        type: "heading",
        text: "Personalization Is Your Biggest Lever",
      },
      {
        type: "paragraph",
        text: "Generic outreach gets ignored. The installers closing at 30%+ rates are using lead data to personalize every touchpoint. When you buy a lead with a high solar score (75+), you know that homeowner has strong solar potential — mention it. Reference their city, their utility provider, recent local incentives.",
      },
      {
        type: "callout",
        text: "Pro tip: Before your first call, spend 2 minutes on Google Maps looking at the homeowner's roof. Note the orientation, any shading, and approximate size. Mentioning \"I noticed your south-facing roof looks ideal for solar\" in your opening line immediately builds credibility.",
      },
      {
        type: "heading",
        text: "Handling the Most Common Objections",
      },
      {
        type: "list",
        items: [
          "\"I'm just researching\" → \"That's exactly why I'm calling — I can give you real numbers for your specific home so your research is based on facts, not estimates.\"",
          "\"It's too expensive\" → \"Most of our customers pay $0 upfront. Can I show you what your monthly payment would look like vs. your current bill?\"",
          "\"I need to talk to my spouse\" → \"Of course — would it be easier if I scheduled a 20-minute call with both of you? I can do evenings or weekends.\"",
          "\"I already got a quote\" → \"Great — I'd love to give you a second opinion. Competition keeps us honest, and you deserve to compare apples to apples.\"",
        ],
      },
      {
        type: "paragraph",
        text: "The installers who consistently close 25–35% of their purchased leads aren't doing anything magical. They're just more systematic, more persistent, and more personalized than the competition. Start with the 6-touch sequence and watch your close rate climb.",
      },
    ],
  },
  {
    slug: "solar-lead-score-explained",
    title: "Solar Lead Score Explained: What 50 vs 90 Actually Means for Your ROI",
    excerpt:
      "Not all leads are created equal. We break down exactly how lead scores are calculated, what each range predicts about close rates, and how to build a buying strategy around score tiers.",
    metaDescription:
      "Understand solar lead scores and their impact on ROI. Learn how 50-64, 65-79, and 80-99 score ranges predict close rates and how to optimize your lead buying strategy.",
    category: "Lead Generation",
    readTime: "6 min read",
    publishedAt: "March 21, 2026",
    author: "Priya Nair",
    authorRole: "Data Analyst, SolarLeadSource",
    authorInitials: "PN",
    authorColor: "bg-amber-500",
    image:
      "https://readdy.ai/api/search-image?query=data%20analytics%20dashboard%20solar%20energy%20metrics%20charts%20graphs%20modern%20office%20clean%20white%20background%20professional%20technology%20green%20accents&width=800&height=450&seq=blog2&orientation=landscape",
    tags: ["lead score", "ROI", "data", "buying strategy"],
    featured: true,
    content: [
      {
        type: "paragraph",
        text: "When you're buying solar leads, the score attached to each lead is the single most important number you'll see. Yet most installers treat all leads the same — same follow-up sequence, same pitch, same urgency level. That's leaving serious money on the table.",
      },
      {
        type: "heading",
        text: "How Lead Scores Are Calculated",
      },
      {
        type: "paragraph",
        text: "Our lead scoring model combines two distinct signals: the Lead Score (how engaged and ready the homeowner is) and the Solar Score (how suitable their property is for solar). Both scores run from 0–100, and together they determine the lead's tier and price.",
      },
      {
        type: "list",
        items: [
          "Lead Score factors: Time since inquiry, number of pages visited, form completion depth, device type, time of day, and whether they've requested quotes before",
          "Solar Score factors: Roof orientation, estimated roof area, local sun hours, utility rates, shading analysis, and estimated system size",
          "Combined scoring: Leads are ranked by a weighted average, with Lead Score weighted 60% and Solar Score 40%",
        ],
      },
      {
        type: "heading",
        text: "What Each Score Range Predicts",
      },
      {
        type: "stat-grid",
        stats: [
          { value: "50–64", label: "Interested — 8–12% avg close rate" },
          { value: "65–79", label: "High Intent — 18–24% avg close rate" },
          { value: "80–99", label: "Ready to Buy — 28–36% avg close rate" },
          { value: "2.4x", label: "ROI difference: 80+ vs 50–64 leads" },
        ],
      },
      {
        type: "paragraph",
        text: "The data is clear: a lead scoring 80+ closes at roughly 3x the rate of a 50–64 lead. But the price difference is only about 40–60%. That means premium leads often deliver better ROI per dollar spent, even though they cost more upfront.",
      },
      {
        type: "heading",
        text: "Building a Score-Based Buying Strategy",
      },
      {
        type: "callout",
        text: "The sweet spot for most installers is the 65–79 range. These leads are priced in the middle tier but close at nearly the same rate as premium leads when worked with a strong follow-up sequence. Many top installers set their threshold at 65 and buy in volume.",
      },
      {
        type: "list",
        items: [
          "New to buying leads? Start with 80+ scores to maximize your close rate while you build your follow-up process",
          "Have a strong sales team? Buy 65–79 leads in volume — the math works out better at scale",
          "Testing a new market? Use 50–64 leads to gauge demand before committing to premium inventory",
          "Scaling fast? Mix tiers: 20% premium (80+), 60% quality (65–79), 20% standard (50–64)",
        ],
      },
    ],
  },
  {
    slug: "northeast-solar-market-2026",
    title: "Northeast Solar Market 2026: Why NJ, CT, and MA Are Exploding Right Now",
    excerpt:
      "Three states are seeing unprecedented solar adoption driven by new incentive stacking, utility rate hikes, and aggressive net metering policies. Here's what installers need to know.",
    metaDescription:
      "Explore the booming Northeast solar market in 2026. Discover why New Jersey, Connecticut, and Massachusetts are seeing record solar adoption and what it means for installers.",
    category: "Market Insights",
    readTime: "7 min read",
    publishedAt: "March 14, 2026",
    author: "James Kowalski",
    authorRole: "Market Research, SolarLeadSource",
    authorInitials: "JK",
    authorColor: "bg-blue-500",
    image:
      "https://readdy.ai/api/search-image?query=northeast%20united%20states%20aerial%20view%20suburban%20homes%20solar%20panels%20rooftops%20autumn%20trees%20new%20england%20architecture%20clean%20sky%20green%20energy%20residential&width=800&height=450&seq=blog3&orientation=landscape",
    tags: ["northeast", "market trends", "NJ", "CT", "MA", "incentives"],
    featured: false,
    content: [
      {
        type: "paragraph",
        text: "If you're a solar installer operating in the Northeast, 2026 is shaping up to be the best year in a decade. Three states in particular — New Jersey, Connecticut, and Massachusetts — are experiencing a perfect storm of demand drivers that's pushing lead volume and close rates to record highs.",
      },
      {
        type: "heading",
        text: "New Jersey: The Incentive Stack Is Unbeatable",
      },
      {
        type: "paragraph",
        text: "New Jersey has always been a strong solar market, but the combination of the federal ITC (30%), the NJ Solar Successor Program, and SREC-II credits is creating a payback period of under 5 years for many homeowners. When you can show a homeowner they'll recoup their investment in 4.5 years and then generate free electricity for 20+ more, the conversation changes completely.",
      },
      {
        type: "heading",
        text: "Connecticut: Utility Rates Are Doing the Selling For You",
      },
      {
        type: "paragraph",
        text: "Connecticut has the second-highest residential electricity rates in the continental US, averaging $0.28/kWh as of Q1 2026. That means a typical 8kW system saves a homeowner $2,400–$3,200 per year. Your pitch practically writes itself.",
      },
      {
        type: "callout",
        text: "Connecticut's Residential Solar Investment Program (RSIP) still has capacity available in 2026. Installers who can navigate the application process have a significant competitive advantage — homeowners who qualify get an additional $0.04–0.06/kWh incentive on top of net metering.",
      },
      {
        type: "heading",
        text: "Massachusetts: SMART Program Driving Consistent Demand",
      },
      {
        type: "paragraph",
        text: "Massachusetts' Solar Massachusetts Renewable Target (SMART) program continues to drive steady demand. The program pays solar owners a fixed rate per kWh generated for 10 years, providing predictable returns that make the financial case easy to present. Block 6 capacity is filling fast — installers who move quickly can lock in customers at the current rate.",
      },
      {
        type: "stat-grid",
        stats: [
          { value: "+34%", label: "NJ lead volume YoY (Q1 2026)" },
          { value: "+28%", label: "CT lead volume YoY (Q1 2026)" },
          { value: "+19%", label: "MA lead volume YoY (Q1 2026)" },
          { value: "$0.28", label: "CT avg electricity rate per kWh" },
        ],
      },
    ],
  },
  {
    slug: "exclusive-vs-shared-leads",
    title: "Exclusive vs. Shared Solar Leads: The Real Cost Comparison",
    excerpt:
      "Shared leads seem cheaper until you do the math. We ran the numbers on 10,000 leads across both models and the results will change how you think about your lead budget.",
    metaDescription:
      "Compare exclusive vs shared solar leads with real data. See why exclusive leads cost 29% less per closed deal despite higher upfront prices. Cost analysis for solar installers.",
    category: "Lead Generation",
    readTime: "5 min read",
    publishedAt: "March 7, 2026",
    author: "Priya Nair",
    authorRole: "Data Analyst, SolarLeadSource",
    authorInitials: "PN",
    authorColor: "bg-amber-500",
    image:
      "https://readdy.ai/api/search-image?query=business%20comparison%20analysis%20two%20paths%20decision%20making%20modern%20office%20clean%20white%20minimal%20professional%20charts%20solar%20energy%20green%20theme&width=800&height=450&seq=blog4&orientation=landscape",
    tags: ["exclusive leads", "shared leads", "cost analysis", "ROI"],
    featured: false,
    content: [
      {
        type: "paragraph",
        text: "The most common question we get from new installers is: \"Why should I pay $130–200 for an exclusive lead when I can get shared leads for $20–40?\" It's a fair question. The answer lies in the math that most lead buyers never actually run.",
      },
      {
        type: "heading",
        text: "The True Cost Per Closed Deal",
      },
      {
        type: "paragraph",
        text: "Let's compare two scenarios with a $5,000 lead budget. Scenario A uses shared leads at $30 each. Scenario B uses exclusive leads at $150 each. The shared leads have a 3% close rate (industry average for shared leads). The exclusive leads have a 22% close rate.",
      },
      {
        type: "stat-grid",
        stats: [
          { value: "167", label: "Shared leads for $5,000 budget" },
          { value: "33", label: "Exclusive leads for $5,000 budget" },
          { value: "5", label: "Closed deals from shared leads (3%)" },
          { value: "7", label: "Closed deals from exclusive leads (22%)" },
        ],
      },
      {
        type: "callout",
        text: "The math: $5,000 ÷ 5 deals = $1,000 cost per acquisition with shared leads. $5,000 ÷ 7 deals = $714 cost per acquisition with exclusive leads. Exclusive leads are actually 29% cheaper per closed deal — and that's before accounting for the time your sales team wastes competing with 4 other installers on every shared lead.",
      },
      {
        type: "heading",
        text: "The Hidden Costs of Shared Leads",
      },
      {
        type: "list",
        items: [
          "Speed-to-call pressure: You're racing 3–5 other installers to reach the homeowner first, which means your team needs to drop everything immediately",
          "Price competition: Homeowners who've been called by multiple installers often use you to negotiate a lower price from their preferred installer",
          "Burnout: Your sales team makes 5x more calls for the same number of deals, leading to higher turnover",
          "Brand damage: Homeowners who feel bombarded by solar calls often leave negative reviews — even for companies they never chose",
        ],
      },
    ],
  },
  {
    slug: "solar-sales-team-hiring-guide",
    title: "How to Build a Solar Sales Team That Closes 25%+ of Purchased Leads",
    excerpt:
      "The difference between a 10% and 25% close rate isn't the leads — it's the team. Here's the hiring profile, training framework, and comp structure that top installers use.",
    metaDescription:
      "Build a solar sales team that closes 25%+ of purchased leads. Learn the hiring profile, 30-day training framework, and compensation structure top installers use.",
    category: "Operations",
    readTime: "9 min read",
    publishedAt: "February 28, 2026",
    author: "Marcus Webb",
    authorRole: "Sales Director, SolarLeadSource",
    authorInitials: "MW",
    authorColor: "bg-[#16a34a]",
    image:
      "https://readdy.ai/api/search-image?query=professional%20sales%20team%20meeting%20modern%20office%20whiteboard%20training%20session%20diverse%20group%20clean%20bright%20interior%20business%20strategy%20green%20accents&width=800&height=450&seq=blog5&orientation=landscape",
    tags: ["hiring", "sales team", "training", "compensation"],
    featured: false,
    content: [
      {
        type: "paragraph",
        text: "We've analyzed close rate data from 200+ installers on our platform. The single biggest predictor of close rate isn't lead quality, market, or price point — it's the quality of the sales team working those leads. Here's what separates the 25%+ closers from the 10% average.",
      },
      {
        type: "heading",
        text: "The Hiring Profile That Works",
      },
      {
        type: "paragraph",
        text: "Stop hiring people with solar experience. Hire people with consultative sales experience — financial advisors, insurance agents, and home improvement salespeople consistently outperform solar veterans because they're trained to sell value and handle objections, not just recite panel specs.",
      },
      {
        type: "list",
        items: [
          "Must-have: 2+ years of phone-based consultative sales (not retail, not door-to-door)",
          "Strong signal: Experience selling high-ticket items ($5,000+) with a 30–90 day sales cycle",
          "Green flag: Has worked with CRM systems and can articulate their personal follow-up process",
          "Red flag: Relies on discounting to close — solar margins are too thin for this approach",
        ],
      },
      {
        type: "heading",
        text: "The 30-Day Training Framework",
      },
      {
        type: "list",
        items: [
          "Week 1: Product knowledge — how solar works, how to read a utility bill, how to calculate savings",
          "Week 2: Objection handling — role-play every objection until responses are automatic",
          "Week 3: Shadow calls — listen to top performers, then do calls with manager listening",
          "Week 4: Live calls with daily debrief — review recordings, identify patterns, adjust",
        ],
      },
      {
        type: "callout",
        text: "The most important training investment: record every call and review them weekly as a team. Installers who do this consistently see close rates improve 8–12 percentage points within 90 days.",
      },
    ],
  },
  {
    slug: "federal-solar-tax-credit-2026",
    title: "Federal Solar Tax Credit 2026: What Installers Need to Know to Close More Deals",
    excerpt:
      "The 30% ITC is still in effect, but there are nuances most installers aren't communicating correctly. Here's how to use the tax credit as a closing tool without creating liability.",
    metaDescription:
      "Master the 30% federal solar tax credit (ITC) for 2026. Learn how to use it as a closing tool, what it covers, and how to communicate it to homeowners without liability.",
    category: "Industry News",
    readTime: "6 min read",
    publishedAt: "February 21, 2026",
    author: "Sarah Chen",
    authorRole: "Policy Analyst, SolarLeadSource",
    authorInitials: "SC",
    authorColor: "bg-rose-500",
    image:
      "https://readdy.ai/api/search-image?query=federal%20tax%20documents%20solar%20energy%20incentives%20government%20policy%20clean%20professional%20office%20desk%20papers%20calculator%20green%20energy%20theme%20minimal&width=800&height=450&seq=blog6&orientation=landscape",
    tags: ["ITC", "tax credit", "federal incentives", "2026"],
    featured: false,
    content: [
      {
        type: "paragraph",
        text: "The 30% federal Investment Tax Credit (ITC) remains one of the most powerful tools in a solar salesperson's arsenal. But it's also one of the most misunderstood — and misrepresented. Here's how to use it correctly to close more deals without creating legal or reputational risk.",
      },
      {
        type: "heading",
        text: "What the ITC Actually Covers in 2026",
      },
      {
        type: "list",
        items: [
          "30% of the total system cost, including equipment, labor, and permitting fees",
          "Battery storage systems installed alongside solar (or retrofitted to existing solar)",
          "Sales tax on equipment in states where it applies",
          "NOT covered: Roof repairs or replacements, even if done to prepare for solar installation",
        ],
      },
      {
        type: "callout",
        text: "Important: The ITC is a tax credit, not a rebate. The homeowner must have sufficient federal tax liability to use it. Always recommend they consult a tax professional — this protects you legally and builds trust.",
      },
      {
        type: "heading",
        text: "Using the ITC as a Closing Tool",
      },
      {
        type: "paragraph",
        text: "The most effective way to present the ITC is in the context of the total 10-year financial picture. Show the homeowner: Year 1 cost after ITC, cumulative savings at Year 5, and total savings at Year 25. When they see the ITC reduces their effective cost by 30% and they break even in under 6 years, the decision becomes much easier.",
      },
      {
        type: "stat-grid",
        stats: [
          { value: "30%", label: "Federal ITC rate through 2032" },
          { value: "26%", label: "Rate drops to in 2033" },
          { value: "$7,500", label: "Avg ITC value on a $25,000 system" },
          { value: "5.8 yrs", label: "Avg payback period with ITC applied" },
        ],
      },
    ],
  },
  {
    slug: "pipeline-management-solar-installers",
    title: "Pipeline Management for Solar Installers: From Lead to Install in 30 Days",
    excerpt:
      "The best installers treat their lead pipeline like a manufacturing process. Here's the stage-by-stage framework that compresses the average sales cycle from 60 days to under 30.",
    metaDescription:
      "Optimize your solar sales pipeline with this 5-stage framework. Learn how top installers compress sales cycles from 60 days to under 30 days with proper pipeline management.",
    category: "Operations",
    readTime: "7 min read",
    publishedAt: "February 14, 2026",
    author: "Marcus Webb",
    authorRole: "Sales Director, SolarLeadSource",
    authorInitials: "MW",
    authorColor: "bg-[#16a34a]",
    image:
      "https://readdy.ai/api/search-image?query=project%20management%20pipeline%20workflow%20kanban%20board%20solar%20installation%20business%20operations%20modern%20office%20clean%20minimal%20professional%20green%20theme&width=800&height=450&seq=blog7&orientation=landscape",
    tags: ["pipeline", "CRM", "operations", "sales cycle"],
    featured: false,
    content: [
      {
        type: "paragraph",
        text: "The average solar sales cycle is 45–60 days from first contact to signed contract. The top 10% of installers on our platform close in under 30 days. The difference isn't luck or market — it's pipeline discipline.",
      },
      {
        type: "heading",
        text: "The 5-Stage Pipeline Framework",
      },
      {
        type: "list",
        items: [
          "Stage 1 — New Lead (Day 0–1): Lead purchased, first contact attempted within 1 hour, CRM record created with all available data",
          "Stage 2 — Contacted (Day 1–3): Two-way conversation established, basic qualification done (homeowner, renter, credit, utility bill size)",
          "Stage 3 — Qualified (Day 3–7): Site assessment scheduled, savings estimate prepared, decision-maker identified",
          "Stage 4 — Proposal Sent (Day 7–14): Custom proposal delivered, follow-up sequence active, objections documented",
          "Stage 5 — Closed / Lost (Day 14–30): Contract signed or lead marked lost with reason code for future analysis",
        ],
      },
      {
        type: "callout",
        text: "The single biggest pipeline killer: leads sitting in Stage 2 for more than 5 days. If you can't get a homeowner to Stage 3 within 5 days of first contact, the probability of closing drops by 60%. Set automatic alerts in your CRM for any lead that hasn't moved stages in 3 days.",
      },
    ],
  },
  {
    slug: "solar-lead-buying-mistakes",
    title: "7 Mistakes Solar Installers Make When Buying Leads (And How to Fix Them)",
    excerpt:
      "After analyzing thousands of lead purchases on our platform, we've identified the patterns that separate profitable buyers from those who give up after their first batch.",
    metaDescription:
      "Avoid the 7 most common solar lead buying mistakes. Learn from data on thousands of lead purchases and discover the patterns that separate profitable buyers from failures.",
    category: "Lead Generation",
    readTime: "6 min read",
    publishedAt: "February 7, 2026",
    author: "Priya Nair",
    authorRole: "Data Analyst, SolarLeadSource",
    authorInitials: "PN",
    authorColor: "bg-amber-500",
    image:
      "https://readdy.ai/api/search-image?query=business%20mistakes%20lessons%20learned%20solar%20energy%20professional%20thinking%20strategy%20clean%20modern%20office%20minimal%20white%20background%20green%20accents%20analytical&width=800&height=450&seq=blog8&orientation=landscape",
    tags: ["mistakes", "lead buying", "strategy", "best practices"],
    featured: false,
    content: [
      {
        type: "paragraph",
        text: "We've watched thousands of installers buy their first batch of leads. Some become consistent, profitable buyers. Others buy once, get frustrated, and swear off purchased leads forever. The difference almost always comes down to the same 7 mistakes.",
      },
      {
        type: "heading",
        text: "Mistake #1: Buying Too Many Leads Before Your Process Is Ready",
      },
      {
        type: "paragraph",
        text: "The most common mistake: buying 50 leads before you have a follow-up sequence, a CRM, or a clear pitch. Start with 5–10 leads, build your process around them, measure your close rate, then scale. Leads don't expire, but your enthusiasm for fixing a broken process does.",
      },
      {
        type: "heading",
        text: "Mistake #2: Setting Your Score Threshold Too Low",
      },
      {
        type: "paragraph",
        text: "New buyers often set their minimum score at 50 to maximize volume. This is backwards. A 50-score lead requires 3x more follow-up effort for the same close rate as a 75-score lead. Start at 70+ until you've proven your process, then experiment with lower thresholds.",
      },
      {
        type: "list",
        items: [
          "Mistake #3: Not calling within the first hour of purchase",
          "Mistake #4: Using a generic pitch instead of referencing the lead's specific data",
          "Mistake #5: Giving up after 2 contact attempts (the average close requires 6+ touchpoints)",
          "Mistake #6: Not tracking which score ranges and states perform best for your team",
          "Mistake #7: Treating a bad week as proof that lead buying doesn't work",
        ],
      },
      {
        type: "callout",
        text: "The installers who succeed with purchased leads treat it like a data problem, not a luck problem. Track everything: contact rate by score range, close rate by state, average days to close, and revenue per lead. After 50 leads, you'll have enough data to optimize aggressively.",
      },
    ],
  },
];
